import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { InventoryVarianceParsedData, InventoryVarianceRawData } from "../../../interfaces/reports/InventoryVarianceReport.interface";
import { MoneyDataItem, PercentDataItem, StringDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";

interface InventoryVarianceReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
}
 
// Moved data processing to a separate function
const processRawData = (rawData: InventoryVarianceRawData[]): InventoryVarianceParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    // Parse the raw data into controlled and formattable types
    return rawData.map((item) => {
        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            parentClientClassCode: item.parentClientClassCode ?? null,
            category: item.category ? item.category == "" ? "N/A" : item.category : "N/A",
            categorySortOrder: item.categorySortOrder ?? null,

            perpetualInventory: new MoneyDataItem(item.perpetualInventory),
            inventory: new MoneyDataItem(item.inv),
            transfersIn: new MoneyDataItem(item.transfersIn),
            transfersInCost: new MoneyDataItem(item.transfersInCost),
            transfersOut: new MoneyDataItem(item.transfersOut),
            transfersOutCost: new MoneyDataItem(item.transfersOutCost),
            inventoryadjustment: new MoneyDataItem(item.inventoryadjustment),
            returnsCost: new MoneyDataItem(item.returnsCost),
            returnsRetail: new MoneyDataItem(item.returnsRetail),
        }
    });
};

const InventoryVarianceReport = ({ clientId, epochMonth }: InventoryVarianceReportProps) => {
    const globalTip = TipContext.useMessage();
    const today = EpochMonthService.getTodayEpochMonth();
    const dataRef = useRef<InventoryVarianceRawData[]>([]);
    const parentDataRef = useRef<InventoryVarianceRawData[]>([]);
    const [parsedData, setParsedData] = useState<InventoryVarianceParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<InventoryVarianceParsedData[]>([]);
    const [sortedData, setSortedData] = useState<InventoryVarianceParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["locationCode", "classCode"]);
    const [trendType, setTrendType] = useState<'MTD' | 'EOM'>('EOM');
    const [pricingType, setPricingType] = useState<"Retail" | "Cost">("Retail");
    const [startEpochMonth, setStartEpochMonth] = useState<number>(epochMonth ? epochMonth - 1 : today - 1);
    const [endEpochMonth, setEndEpochMonth] = useState<number>(epochMonth ?? today);

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-1 py-2 bg-gray-500 text-white"
                : "px-1 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    // cols: Cash Margin, Gross Margin, CMROI, GMROI, Turn, Efficiency, Freshness

    const columns = [
        ...hierarchyColumns,
        { name: "Actual", sortable: true },
        { name: "Perpetual", sortable: true },
        { name: "Variance", sortable: true },
        { name: "% Diff", sortable: true },
        { name: "Transfers In", sortable: true },
        { name: "Transfers Out", sortable: true },
        { name: "RTV's", sortable: true },
        { name: "Inventory Adj.", sortable: true },
    ];

    useEffect(() => {
        setStartEpochMonth(endEpochMonth - 1);
    }, [endEpochMonth]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            ReportService.getInventoryVarianceReportData({ clientId, startEpochMonth, endEpochMonth, isMtd: trendType === 'MTD' }).then((response) => {
                if (response) {
                    // Store raw data in refs
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    // Process both child and parent data
                    const processed = processRawData(response.data ?? []);
                    const processedParent = processRawData(response.parentLevelData ?? []);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    // Default: use child data
                    let sorted = ReportService.groupByHierarchy(processed, hierarchy ?? ["locationCode", "classCode"]);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch((error) => {
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, startEpochMonth, endEpochMonth, trendType]);

    useEffect(() => {
        // Use parent or child data based on hierarchy
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter]);

    const tableData = useMemo(() => {
        const isCost = pricingType === "Cost";

        return sortedData.map((item, index) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);

            const pctDiff = (item.perpetualInventory.value ? (Number(item.inventory.value) - Number(item.perpetualInventory.value)) / Number(item.perpetualInventory.value) : 0) * 100;

            return [
                ...labelRows,
                item.inventory.formatted,
                item.perpetualInventory.formatted,
                ReportService.formatCurrency(Number(item.inventory?.value) - Number(item.perpetualInventory.value)),
                ReportService.formatPercent(pctDiff),
                isCost ? item.transfersInCost.formatted : item.transfersIn.formatted,
                isCost ? item.transfersOutCost.formatted : item.transfersOut.formatted,
                isCost ? item.returnsCost.formatted : item.returnsRetail.formatted,
                item.inventoryadjustment.formatted,
            ];
        });
    }, [sortedData, hierarchy, pricingType]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`${EpochMonthService.epochMonthToMMMYY(startEpochMonth)} - ${EpochMonthService.epochMonthToMMMYY(endEpochMonth)} Inventory Variance Report`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="w-full h-full">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title="Inventory Variance Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Inventory_Variance_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = processRawData(dataRef.current);
                            setParsedData(parsedData);
                            let parsedParentData = processRawData(parentDataRef.current);
                            setParentParsedData(parsedParentData);
                        }}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Start Period</span>
                            <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={endEpochMonth - 12} maxMonth={endEpochMonth} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">End Period</span>
                            <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={today - 11} maxMonth={today} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Trend Type</span>
                            <select
                                value={trendType}
                                onChange={e => {
                                    const val = e.target.value;
                                    setTrendType(val === 'MTD' ? 'MTD' : 'EOM');
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="MTD">MTD</option>
                                <option value="EOM">EOM</option>
                            </select>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Pricing Type</span>
                            <select
                                value={pricingType}
                                onChange={e => {
                                    const val = e.target.value;
                                    setPricingType(val === 'Retail' ? 'Retail' : 'Cost');
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="Retail">Retail</option>
                                <option value="Cost">Cost</option>
                            </select>
                        </div>
                    </div>
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface InventoryVarianceReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const InventoryVarianceReportModal = ({ clientId, epochMonth, onClose }: InventoryVarianceReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <InventoryVarianceReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    InventoryVarianceReport,
    InventoryVarianceReportModal
}